import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import axios from "axios";
import { Clock, ChevronRight, Search, Sparkles, Twitter, Facebook, Linkedin, Link2, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/lib/constants";

const SEO = {
  title: "Digital Marketing Blog Dallas | SEO, PPC & Social Media Tips | DFW Marketing Insights",
  description: "Expert digital marketing blog from Dallas's top agency. Learn Local SEO strategies, Google Ads tips, social media marketing tactics, content marketing best practices & lead generation techniques for DFW businesses.",
  keywords: "Dallas SEO blog, digital marketing tips Dallas, PPC strategies DFW, social media marketing blog, content marketing guide Dallas, local SEO tips Texas, Google Ads blog Dallas, marketing insights Fort Worth",
  image: "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG",
  url: "https://digitalmedianinja.com/blog"
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categories, setCategories] = useState(["All"]);

  useEffect(() => {
    document.title = SEO.title;
    
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog/posts`);
        const data = response.data || [];
        setPosts(data);
        setFilteredPosts(data);
        
        // Extract unique categories
        const uniqueCategories = ["All", ...new Set(response.data.map(p => p.category))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Filter posts when search or category changes
  useEffect(() => {
    let filtered = posts;
    
    if (selectedCategory !== "All") {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        (p.title && p.title.toLowerCase().includes(term)) || 
        (p.excerpt && p.excerpt.toLowerCase().includes(term)) ||
        (p.tags && p.tags.some(t => t && t.toLowerCase().includes(term)))
      );
    }
    
    setFilteredPosts(filtered);
  }, [searchTerm, selectedCategory, posts]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-[#00E676] animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="blog-page">
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <meta name="keywords" content={SEO.keywords} />
        <link rel="canonical" href={SEO.url} />
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.description} />
        <meta property="og:image" content={SEO.image} />
        <meta property="og:url" content={SEO.url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO.title} />
        <meta name="twitter:description" content={SEO.description} />
      </Helmet>

      <div className="container-custom">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#00E676] uppercase tracking-wider text-sm font-medium">Blog</span>
          <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Digital Marketing Insights
          </h1>
          <p className="text-gray-400 text-lg">
            Stay ahead with the latest strategies in SEO, AEO, GEO, and AI-powered marketing.
          </p>
        </div>

        {/* Featured Article - Emergent */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <span className="text-purple-400 font-medium">Featured Article</span>
          </div>
          <Link to="/blog/emergent-ai-app-builder-build-app-for-free">
            <div className="relative bg-gradient-to-br from-purple-500/10 via-cyan-500/10 to-emerald-500/10 border border-purple-500/30 rounded-2xl overflow-hidden group hover:border-purple-500/50 transition-all">
              <div className="grid md:grid-cols-2 gap-6 p-6 md:p-8">
                <div className="flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-400 text-sm w-fit mb-4">
                    <Rocket className="w-4 h-4" />
                    $100M+ Funded • AI Technology
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors">
                    Build an App for Free with Emergent AI
                  </h2>
                  <p className="text-gray-400 mb-4">
                    Discover how 5+ million users are building production-ready apps without coding. Compare Emergent vs Lovable vs Cursor vs Bolt in our comprehensive 2026 guide.
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-gray-500">
                      <Clock className="w-4 h-4" /> 12 min read
                    </span>
                    <span className="text-purple-400 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read Article <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
                <div className="relative h-48 md:h-auto rounded-xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1677442135136-760c813028c0?w=800&h=500&fit=crop"
                    alt="Emergent AI App Builder"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
              data-testid="blog-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className={selectedCategory === category ? "btn-ninja" : "btn-outline"}
                onClick={() => setSelectedCategory(category)}
                data-testid={`category-${category.toLowerCase().replace(/\s/g, '-')}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <p className="text-gray-500 mb-6">
          Showing {filteredPosts.length} of {posts.length} articles
          {selectedCategory !== "All" && ` in ${selectedCategory}`}
        </p>

        {/* Blog Grid */}
        {filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link 
                to={`/blog/${post.slug}`} 
                key={post.slug}
                className="block"
                data-testid={`blog-card-link-${post.id}`}
              >
                <Card 
                  className="bg-[#0A0A0A] border-white/5 hover:border-[#00E676]/30 transition-all overflow-hidden group cursor-pointer h-full"
                  data-testid={`blog-card-${post.id}`}
                >
                  <div className="relative h-48">
                    <img 
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#00E676]/20 text-[#00E676] text-sm">
                      {post.category}
                    </span>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {post.read_time} min read
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-3 group-hover:text-[#00E676] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-400 text-sm line-clamp-3 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(post.tags || []).slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded bg-white/5 text-gray-400">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="text-[#00E676] group-hover:text-[#00C853] inline-flex items-center">
                      Read Article <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">No articles found matching your criteria.</p>
            <Button 
              className="btn-outline mt-4" 
              onClick={() => { setSearchTerm(""); setSelectedCategory("All"); }}
            >
              Clear Filters
            </Button>
          </div>
        )}

        {/* Newsletter CTA */}
        <div className="mt-20 text-center glass rounded-2xl p-12">
          <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Get weekly insights on AEO, GEO, and AI-powered marketing strategies delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input placeholder="Enter your email" className="form-input" />
            <Button className="btn-ninja">Subscribe</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
